import { SbkBetSlip, Selection } from '@/feature/betslip-sbk/types';
import { BetIds } from '@/feature/betslip-sbk/utils/betslip-utils';
import { retainReferrer } from '@/feature/betslip-sbk/utils/share-referrer';

/**
 * Remove selection from bet slip
 * @param state - current bet slip state
 * @param selectionId - selection id to remove
 */
export const removeSelection = (
    state: Pick<
        SbkBetSlip,
        | 'bets'
        | 'selections'
        | 'options'
        | 'markets'
        | 'events'
        | 'eventOrder'
        | 'selectionOrder'
        | 'showKeyboard'
        | 'editBetId'
        | 'oddsChanges'
        | 'sgpOddsChanges'
        | 'sgpOdds'
        | 'sgpEventDisabled'
        | 'isBetrBucksEnabled'
        | 'selectedPromos'
        | 'isAutoPromoDismissed'
        | 'referrer'
    >,
    selectionId: string
) => {
    const selection = state.selections[selectionId];
    if (!selection) {
        return {};
    }

    const options = { ...state.options };
    delete options[selection.optionId];

    const selections = { ...state.selections };
    delete selections[selectionId];

    // remove market if no selections left
    const markets = { ...state.markets };
    if (!Object.values(selections).find(s => s.marketId === selection.marketId)) {
        delete markets[selection.marketId];
    }

    // remove event and eventOrder if no selections left
    const events = { ...state.events };
    const sgpEventDisabled = { ...state.sgpEventDisabled };
    let eventOrder = [...state.eventOrder];
    const remainingSelectionsForEvent = Object.values(selections).filter(s => s.eventId === selection.eventId);

    if (remainingSelectionsForEvent.length === 0) {
        delete events[selection.eventId];
        delete sgpEventDisabled[selection.eventId];
        eventOrder = removeEventOrder(selection.eventId, state.eventOrder);
    } else if (remainingSelectionsForEvent.length === 1 && sgpEventDisabled[selection.eventId]) {
        // Re-enable the last remaining selection when SGP is disabled for the event
        const lastSelection = remainingSelectionsForEvent[0];
        selections[lastSelection.id] = {
            ...lastSelection,
            isComboEnabled: true,
        };
    }

    const bets = removeBets(selection, state.bets, state.selections);

    // remove isBetrBucksEnabled entry for the removed selection
    const isBetrBucksEnabled = { ...state.isBetrBucksEnabled };
    delete isBetrBucksEnabled[BetIds.SINGLE(selection)];
    // remove combo entry if combo bet is removed
    if (Object.keys(selections).length <= 1) {
        delete isBetrBucksEnabled[BetIds.COMBO];
    }

    // remove the promo attached to the removed selection's single bet
    const selectedPromos = { ...state.selectedPromos };
    delete selectedPromos[BetIds.SINGLE(selection)];
    // remove combo promo if combo bet is removed
    if (Object.keys(selections).length <= 1) {
        delete selectedPromos[BetIds.COMBO];
    }

    const selectionOrder = state.selectionOrder.filter(id => id !== selectionId);

    const isAutoPromoDismissed = Object.keys(selections).length === 0 ? false : state.isAutoPromoDismissed;

    // hide keyboard if editing bet is removed
    let showKeyboard = state.showKeyboard;
    if (state.showKeyboard && state.editBetId === BetIds.SINGLE(selection)) {
        showKeyboard = false;
    }

    // remove odds change if selection is removed
    const oddsChanges = { ...state.oddsChanges };
    delete oddsChanges[selectionId];

    // remove sgp odds change if selection is removed
    const sgpOddsChanges = Object.keys(state.sgpOddsChanges).reduce<SbkBetSlip['sgpOddsChanges']>((acc, key) => {
        if (!key.includes(selectionId)) {
            acc[key] = state.sgpOddsChanges[key];
        }
        return acc;
    }, {});

    // remove sgp odds entries that include the removed selection
    const sgpOdds = Object.keys(state.sgpOdds).reduce<SbkBetSlip['sgpOdds']>((acc, key) => {
        if (!key.includes(selectionId)) {
            acc[key] = state.sgpOdds[key];
        }
        return acc;
    }, {});

    // remove editBetId if selection is removed
    const editBetId = state.editBetId === selectionId ? null : state.editBetId;

    return {
        options,
        selections,
        markets,
        events,
        bets,
        isBetrBucksEnabled,
        selectedPromos,
        isAutoPromoDismissed,
        selectionOrder,
        eventOrder,
        showKeyboard,
        oddsChanges,
        editBetId,
        sgpOddsChanges,
        sgpOdds,
        sgpEventDisabled,
        referrer: retainReferrer(state.referrer, selections),
    };
};

/**
 * Re-enables the sole remaining disabled selection for an event after the enabled selection was removed.
 * Only applies when exactly one selection remains — multiple remaining selections should go
 * through revalidateSelectionsAfterRemoval to check compatibility via the SGP API.
 * Returns the updated selections slice, or null if not applicable.
 */
export const reEnableLastDisabledSelection = (
    state: Pick<SbkBetSlip, 'selections'>,
    allSelectionsForEvent: Selection[]
): Pick<SbkBetSlip, 'selections'> | null => {
    if (allSelectionsForEvent.length !== 1 || allSelectionsForEvent[0].isComboEnabled) {
        return null;
    }
    const sel = allSelectionsForEvent[0];
    return {
        selections: {
            ...state.selections,
            [sel.id]: { ...state.selections[sel.id], isComboEnabled: true },
        },
    };
};

/**
 * Removes the necessary bets from bet slip state when a selection is removed
 * @param selection - the selection to be removed
 * @param bets - the current bets
 * @param selections - the current selections
 */
export const removeBets = (selection: Selection, bets: SbkBetSlip['bets'], selections: SbkBetSlip['selections']) => {
    const updatedBets = { ...bets };
    delete updatedBets[BetIds.SINGLE(selection)];
    if (Object.keys(selections).length <= 2) {
        delete updatedBets[BetIds.COMBO];
    }
    return updatedBets;
};

/**
 * Remove eventId from eventOrder.
 * @param eventId - the eventId to be removed from eventOrder
 * @param eventOrder - bet slip state's eventOrder
 */
export const removeEventOrder = (eventId: string, eventOrder: SbkBetSlip['eventOrder']) => {
    return eventOrder.filter(event => event !== eventId);
};
